import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Loader2, ChevronLeft, ChevronRight, Users2, AlertTriangle } from 'lucide-react';
import { crmWorkload, listDepartments } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const DAY_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const hoursOf = (min) => Math.round((min / 60) * 10) / 10;

const heatColor = (minutes) => {
  if (minutes === 0) return 'bg-white';
  if (minutes < 120) return 'bg-emerald-50';
  if (minutes < 240) return 'bg-emerald-100';
  if (minutes < 360) return 'bg-amber-100';
  if (minutes < 480) return 'bg-amber-200';
  return 'bg-red-200';
};

const WorkloadPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [depts, setDepts] = useState([]);
  const [deptId, setDeptId] = useState('');
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
    return d.toISOString().slice(0, 10);
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await crmWorkload({
        ...(deptId ? { department_id: deptId } : {}),
        week_start: weekStart,
      });
      setData(r);
    } catch { toast({ title: 'Failed to load workload', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [deptId, weekStart]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listDepartments().then((r) => setDepts(r.data || [])).catch(() => {});
  }, []);

  const shift = (n) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const rows = data?.rows || [];
  const days = data?.days || [];

  const totals = useMemo(() => {
    const t = { minutes: 0, count: 0, overdue: 0 };
    rows.forEach((r) => {
      t.minutes += r.total_minutes;
      t.count += r.total_count;
      days.forEach((d) => { t.overdue += r.cells[d]?.overdue || 0; });
    });
    return t;
  }, [rows, days]);

  return (
    <div data-testid="workload-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">CRM · Team</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            Workload
          </h1>
          <p className="mt-1.5 text-sm text-[#0b1a2e]/60">
            Who has how much on their plate this week. Estimated hours per open task.
            {totals.overdue > 0 && <span className="text-red-700 font-semibold"> · {totals.overdue} overdue across team</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={deptId} onChange={(e) => setDeptId(e.target.value)} data-testid="workload-dept-filter"
            className="px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-sm">
            <option value="">All departments</option>
            {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button onClick={() => shift(-1)} className="p-1.5 rounded-full bg-white border border-[#0f5c3d]/20"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-sm text-[#0b1a2e] font-medium min-w-[110px] text-center">Week of {weekStart}</div>
          <button onClick={() => shift(1)} className="p-1.5 rounded-full bg-white border border-[#0f5c3d]/20"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? (
        <div className="p-16 bg-white rounded-2xl text-center text-[#0b1a2e]/50">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading workload…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-12 bg-white rounded-2xl text-center border border-black/5">
          <Users2 className="w-10 h-10 mx-auto mb-3 text-[#0b1a2e]/30" />
          <div className="text-lg font-medium text-[#0b1a2e]">No staff match this filter</div>
          <p className="text-sm text-[#0b1a2e]/60 mt-1">Try widening the department filter or the week window.</p>
        </div>
      ) : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="workload-table">
              <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
                <tr>
                  <th className="text-left px-4 py-2 sticky left-0 bg-[#f7fbf8] z-10">Staff</th>
                  {days.map((d, i) => (
                    <th key={d} className="text-center px-2 py-2 min-w-[64px]">
                      <div>{DAY_LABEL[i]}</div>
                      <div className="text-[9px] font-normal">{d.slice(5)}</div>
                    </th>
                  ))}
                  <th className="text-right px-4 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-black/5" data-testid="workload-row">
                    <td className="px-4 py-2 sticky left-0 bg-white z-10">
                      <div className="font-medium text-[#0b1a2e]">{r.name}</div>
                    </td>
                    {days.map((d) => {
                      const c = r.cells[d] || { count: 0, minutes: 0, overdue: 0 };
                      return (
                        <td key={d} className={`text-center px-2 py-2 ${heatColor(c.minutes)}`}>
                          {c.count > 0 ? (
                            <div className="flex flex-col items-center leading-tight">
                              <span className="text-sm font-semibold text-[#0b1a2e]">{hoursOf(c.minutes)}h</span>
                              <span className="text-[10px] text-[#0b1a2e]/60">{c.count} task{c.count !== 1 ? 's' : ''}</span>
                              {c.overdue > 0 && (
                                <span className="text-[10px] text-red-700 inline-flex items-center gap-0.5">
                                  <AlertTriangle className="w-2.5 h-2.5" /> {c.overdue}
                                </span>
                              )}
                            </div>
                          ) : <span className="text-[#0b1a2e]/20">—</span>}
                        </td>
                      );
                    })}
                    <td className="text-right px-4 py-2">
                      <div className="text-sm font-semibold text-[#0b1a2e]">{hoursOf(r.total_minutes)}h</div>
                      <div className="text-[10px] text-[#0b1a2e]/60">{r.total_count} open</div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#f7fbf8]">
                <tr>
                  <td className="px-4 py-2 font-semibold text-[#0b1a2e]">Team total</td>
                  <td colSpan={days.length} className="px-2 py-2 text-center text-[#0b1a2e]/70 text-xs">
                    {totals.count} open tasks · {totals.overdue > 0 && <span className="text-red-700 font-semibold">{totals.overdue} overdue</span>}
                  </td>
                  <td className="text-right px-4 py-2 font-semibold text-[#0b1a2e]">{hoursOf(totals.minutes)}h</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-3 text-xs text-[#0b1a2e]/60">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50" /> Light</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100" /> Comfortable</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100" /> Busy</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200" /> Heavy</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200" /> Overloaded (8h+)</span>
      </div>
    </div>
  );
};

export default WorkloadPage;
